import * as React from 'react';
import { IconButton } from '@material-ui/core';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import AddIcon from '@mui/icons-material/Add';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { getMyUser, isAdministrator } from '../../../store/session/selectors';
import { getGeneralSettings, getSettings } from '../../../store/settings/selectors';
import { loadSettings } from '../../../store/settings/actions';
import { getCashRegisterEntriesAsync } from '../../../ajax/requests';
import { CashRegisterEntryData } from '../../../models';
import { formatEuros } from '../../../shared/string';
import { resolveCashRegisterScope, entryBelongsToScope, cashRegisterPaymentTypes, pendingBalanceOf } from '../../../shared/appointmint/cashRegister';
import { Selectors as GeneralSelectors, Actions as GeneralActions } from '../../../store/general';
import CashRegisterQuickAction from '../../../pages/Home/HomeCalendar/CashRegisterQuickAction';
import moment from 'moment';
import AppButton from '../../../components/AppButton';
import { ComboField } from '../../../components/Forms/fields/ComboField';
import CashRegisterProcessModal from '../../../components/CashRegister/CashRegisterProcessModal';

const StyledDropdownMenu = styled(DropdownMenu)`
    && {
        width: min(92vw, 22rem);
        max-height: 28rem;
        display: none;
        overflow: hidden;
        padding: var(--spacer-XS);
        border: var(--border-thin) solid var(--surface-border-subtle);
        border-radius: 0.7rem;
        box-shadow: 0 12px 28px rgba(15, 34, 34, 0.16);
        background: var(--brand-accent);
    }

    &&.show {
        display: flex;
        flex-direction: column;
    }
`;

const MenuBody = styled.div`
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding-right: var(--spacer-XXS);
`;

const MenuFooter = styled.div`
    flex-shrink: 0;
    margin-top: var(--spacer-XS);
    padding-top: var(--spacer-XS);
    border-top: var(--border-thin) solid var(--surface-border-subtle);
`;

const SectionHeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--spacer-XS) var(--spacer-S);
`;

const SectionHeaderLeft = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
`;

const RegisterNameLabel = styled.span`
    color: var(--text-strong);
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: none;
`;

const SwitchRegisterButton = styled(IconButton)`
    && {
        padding: 0.2rem;
        color: var(--text-muted);
        flex-shrink: 0;
    }
`;

const EntryRow = styled(DropdownItem)`
    && {
        white-space: normal;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--spacer-S);
        padding: var(--spacer-S);
        margin-bottom: var(--spacer-XXS);
        border-radius: 0.6rem;
        border: var(--border-thin) solid transparent;
        transition: background-color 0.15s ease, border-color 0.15s ease;

        &:hover,
        &:focus {
            background-color: var(--surface-brand-soft);
            border-color: var(--surface-border-subtle);
        }
    }
`;

const PaymentLabel = styled.span`
    flex: 1;
    font-size: 0.85rem;
    color: var(--text-strong);
`;

const PaymentAmount = styled.strong`
    font-size: 0.85rem;
    color: var(--brand-secondary);
    flex-shrink: 0;
`;

const OpenRowButton = styled(IconButton)`
    && {
        padding: var(--spacer-XXS);
        border-radius: 0.35rem;
        background-color: var(--brand-secondary);
        color: var(--brand-accent);
        flex-shrink: 0;

        svg {
            font-size: 0.95rem;
        }

        &:hover {
            background-color: var(--brand-secondary);
            opacity: 0.85;
        }
    }
`;

const EmptyState = styled(DropdownItem)`
    && {
        color: var(--text-muted);
        padding: var(--spacer-S);
    }
`;

const FooterRow = styled(DropdownItem)`
    && {
        border-radius: 0.6rem;
        padding: var(--spacer-S);
    }
`;

const FooterContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacer-S);
`;

const ViewRegisterButton = styled(Button)`
    && {
        border: 0;
        background: none;
        padding: 0;
        color: var(--brand-secondary);
        font-weight: 700;
        text-align: center;
        flex: 1;
        text-decoration: none;
    }
`;

const QuickSaleButton = styled(IconButton)`
    && {
        padding: 0.25rem;
        color: var(--text-muted);
    }
`;

const PendingSummary = styled.div`
    font-size: 0.75rem;
    color: var(--text-muted);
    margin-top: var(--spacer-XXS);
`;

const CashRegisterMenu: React.FC = () => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const currentUser = useSelector(getMyUser);
    const isAdmin = useSelector(isAdministrator);
    const settings = useSelector(getSettings);
    const generalSettings = useSelector(getGeneralSettings);

    const [isOpen, setOpen] = React.useState(false);
    const [quickSaleOpen, setQuickSaleOpen] = React.useState(false);
    const [switchRegisterOpen, setSwitchRegisterOpen] = React.useState(false);
    const [manageType, setManageType] = React.useState<string | null>(null);
    const [pendingRegisterName, setPendingRegisterName] = React.useState<string>('');
    const [entries, setEntries] = React.useState<CashRegisterEntryData[]>([]);
    const [isLoading, setLoading] = React.useState(false);

    const selectedCashRegisterName = useSelector(GeneralSelectors.getSelectedCashRegisterName);
    const sharedRegisterNames = settings.cashRegisterSettings?.sharedRegisterNames ?? [];

    const canApprove = React.useMemo(() => {
        if (isAdmin) return true;
        const nonAdminApprovers = settings.cashRegisterSettings?.nonAdminApprovers ?? [];
        return currentUser?.username ? nonAdminApprovers.includes(currentUser.username) : false;
    }, [isAdmin, settings.cashRegisterSettings?.nonAdminApprovers, currentUser?.username]);

    const registerScope = React.useMemo(
        () => resolveCashRegisterScope(selectedCashRegisterName, currentUser?.username),
        [selectedCashRegisterName, currentUser?.username],
    );

    const loadEntries = React.useCallback(() => {
        const from = moment().subtract(30, 'days').startOf('day').format('YYYY-MM-DDTHH:mm:ss');
        const to = moment().endOf('day').format('YYYY-MM-DDTHH:mm:ss');
        setLoading(true);
        return getCashRegisterEntriesAsync(from, to)
            .then(data => setEntries(data.filter(e => entryBelongsToScope(e, registerScope))))
            .catch(() => setEntries([]))
            .finally(() => setLoading(false));
    }, [registerScope]);

    React.useEffect(() => {
        loadEntries();
    }, [loadEntries]);

    React.useEffect(() => {
        if (isOpen) {
            dispatch(loadSettings());
            loadEntries();
        }
    }, [isOpen, loadEntries]);

    const pendingEntries = React.useMemo(() => entries.filter(e => !e.handledByUser), [entries]);
    const pendingCount = pendingEntries.length;
    const pendingTotal = React.useMemo(() => pendingEntries.reduce((sum, e) => sum + e.paymentValue, 0), [pendingEntries]);
    // Every configured payment method is always listed, even at a zero
    // balance, unioned with any type actually seen on the entries.
    const balancesByType = React.useMemo(
        () => cashRegisterPaymentTypes(entries, generalSettings.paymentMethods)
            .map(type => ({ type, value: pendingBalanceOf(entries, type) })),
        [entries, generalSettings.paymentMethods],
    );

    const openManage = (type: string) => {
        setOpen(false);
        setManageType(type);
    };

    const displayName = selectedCashRegisterName || t(TK.cashRegisterOwnRegister);

    return (
        <>
            <Dropdown
                className="notificationsMenuContainer"
                isOpen={isOpen}
                toggle={() => setOpen(!isOpen)}
            >
                <DropdownToggle nav className="notificationsButton">
                    <IconButton>
                        <PointOfSaleIcon />
                    </IconButton>
                </DropdownToggle>

                <StyledDropdownMenu right>
                    <MenuBody>
                        <SectionHeaderRow>
                            <SectionHeaderLeft>
                                <RegisterNameLabel>
                                    {sharedRegisterNames.length > 0
                                        ? (selectedCashRegisterName || t(TK.cashRegisterOwnRegister))
                                        : t(TK.cashRegisterPending)
                                    }
                                </RegisterNameLabel>
                                {pendingCount > 0 && (
                                    <PendingSummary>
                                        {pendingCount} {t(TK.cashRegisterTransactions)} · {formatEuros(pendingTotal)}
                                    </PendingSummary>
                                )}
                            </SectionHeaderLeft>
                            {sharedRegisterNames.length > 0 && (
                                <SwitchRegisterButton
                                    size="small"
                                    title={t(TK.cashRegisterChangeRegister)}
                                    onClick={(e: React.MouseEvent) => {
                                        e.stopPropagation();
                                        setPendingRegisterName(selectedCashRegisterName ?? '');
                                        setSwitchRegisterOpen(true);
                                    }}
                                >
                                    <SwapHorizIcon fontSize="small" />
                                </SwitchRegisterButton>
                            )}
                        </SectionHeaderRow>

                        {balancesByType.length === 0 ? (
                            <EmptyState disabled>
                                {isLoading ? `${t(TK.loading)}...` : t(TK.cashRegisterNoPendingEntries)}
                            </EmptyState>
                        ) : (
                            balancesByType.map(({ type, value }) => (
                                <EntryRow key={type} toggle={false} onClick={() => openManage(type)}>
                                    <PaymentLabel>{type}</PaymentLabel>
                                    <PaymentAmount>{formatEuros(value)}</PaymentAmount>
                                    <OpenRowButton size="small" tabIndex={-1}>
                                        <ArrowForwardIcon fontSize="small" />
                                    </OpenRowButton>
                                </EntryRow>
                            ))
                        )}
                    </MenuBody>

                    <MenuFooter>
                        <FooterRow tag="div">
                            <FooterContent>
                                <ViewRegisterButton
                                    color="link"
                                    onClick={() => { setOpen(false); setQuickSaleOpen(true); }}
                                >
                                    {t(TK.cashRegisterAddPayment)}
                                </ViewRegisterButton>
                                <QuickSaleButton
                                    size="small"
                                    title={t(TK.cashRegisterQuickSale)}
                                    onClick={() => { setOpen(false); setQuickSaleOpen(true); }}
                                >
                                    <AddIcon fontSize="small" />
                                </QuickSaleButton>
                            </FooterContent>
                        </FooterRow>
                    </MenuFooter>
                </StyledDropdownMenu>
            </Dropdown>

            <CashRegisterQuickAction
                controlledOpen={quickSaleOpen}
                onControlledClose={() => setQuickSaleOpen(false)}
            />

            <Modal isOpen={switchRegisterOpen} toggle={() => setSwitchRegisterOpen(false)}>
                <ModalHeader toggle={() => setSwitchRegisterOpen(false)}>
                    {t(TK.cashRegisterChangeRegister)}
                </ModalHeader>
                <ModalBody>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: 'var(--spacer-M)' }}>
                        {t(TK.cashRegisterSelectRegister)}
                    </p>
                    <ComboField
                        label={t(TK.cashRegisterCurrentRegister)}
                        value={pendingRegisterName}
                        options={[
                            { value: '', label: t(TK.cashRegisterOwnRegister) },
                            ...sharedRegisterNames.map(name => ({ value: name, label: name })),
                        ]}
                        onChange={value => setPendingRegisterName(value ?? '')}
                    />
                </ModalBody>
                <ModalFooter>
                    <AppButton color="secondary" onClick={() => setSwitchRegisterOpen(false)}>
                        {t(TK.cancel)}
                    </AppButton>
                    <AppButton
                        color="primary"
                        onClick={() => {
                            dispatch(GeneralActions.selectCashRegisterName(pendingRegisterName));
                            setSwitchRegisterOpen(false);
                        }}
                    >
                        {t(TK.confirm)}
                    </AppButton>
                </ModalFooter>
            </Modal>

            {manageType && (
                <CashRegisterProcessModal
                    isOpen
                    register={registerScope}
                    displayName={displayName}
                    initialPaymentType={manageType}
                    canApprove={canApprove}
                    sharedRegisterNames={sharedRegisterNames}
                    onAfterAction={loadEntries}
                    onClose={() => { setManageType(null); loadEntries(); }}
                    onSwitchRegister={sharedRegisterNames.length > 0 ? () => { setPendingRegisterName(selectedCashRegisterName ?? ''); setSwitchRegisterOpen(true); } : undefined}
                />
            )}
        </>
    );
};

export default CashRegisterMenu;
